import React, { useState, useEffect } from "react";
import { Box, Button, IconButton, Link, Typography, Container, Paper } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { styled } from "@mui/material/styles";

const COOKIE_KEY = "autosocializer-cookie-consent";
const EXPIRATION_TIME = 180 * 24 * 60 * 60 * 1000; 

const BannerWrapper = styled(Paper)(({ theme }) => ({
	position: "fixed",
	bottom: 0,
	left: 0,
	right: 0,
	zIndex: 1300,
	borderRadius: 0,
	borderTop: `1px solid ${theme.palette.divider}`,
	padding: theme.spacing(3, 0),
	backgroundColor: "#ffffff",
	boxShadow: "0px -4px 20px rgba(0, 0, 0, 0.05)",
}));

const CookieBanner: React.FC = () => {
	const [showConsent, setShowConsent] = useState(false);

	useEffect(() => {
		if (typeof window === "undefined") return;

		const raw = localStorage.getItem(COOKIE_KEY);
		if (!raw) {
			setShowConsent(true);
			return;
		}

		try {
			const { value, timestamp } = JSON.parse(raw);
			const isExpired = Date.now() - timestamp > EXPIRATION_TIME;

			if (isExpired || value === "rejected") {
				setShowConsent(true);
			} else if (value === "accepted") {
				(window as any).clarity?.("consent");
			}
		} catch (e) {
			setShowConsent(true);
		}
	}, []);

	const handleConsent = (value: "accepted" | "rejected") => {
		if (typeof window === "undefined") return;

		localStorage.setItem(
			COOKIE_KEY,
			JSON.stringify({ value, timestamp: Date.now() })
		);

		if (value === "accepted") {
			(window as any).clarity?.("consent");
		} else {
			(window as any).clarity?.("consent", false);
		}

		setShowConsent(false);
	};

	if (!showConsent) return null;

	return (
		<BannerWrapper elevation={0} sx={{maxWidth: "100dvw",}}>
				<ContainerBlock sx={{ position: "relative" }}>
				<IconButton
					aria-label="Close"
					onClick={() => handleConsent("rejected")}
					sx={{
						position: "absolute",
						top: -12,
						right: 0,
						color: "text.secondary",
					}}
					size="small"
				>
					<CloseIcon fontSize="small" />
				</IconButton>

				<Grid container spacing={2} alignItems="center">
					<Grid size={{xs:12,md:8}}>
						<Typography
						 component="div"
							variant="subtitle1"
							sx={{ fontWeight: 700, color: "text.primary", mb: 0.5 }}
						>
							We value your privacy
						</Typography>
						<Typography
						 component="p"
							variant="body2"
							sx={{ color: "text.secondary", lineHeight: 1.6 }}
						>
							We use Microsoft Clarity for analytics to improve your experience. 
                            By clicking "Accept", you agree to our use of tracking technology. 
                            Read our{" "}
							<Link
								href="/privacy-policy"
								sx={{ color: "#9300FF", fontWeight: 600, textDecoration: "none", "&:hover": { textDecoration: "underline" } }}
							>
								Privacy Policy
							</Link>
							.
						</Typography>
					</Grid>
					<Grid size={{xs:12,md:4}}>
						<Box
							display="flex"
							gap={2}
							sx={{ justifyContent: { xs: "flex-start", md: "flex-end" } }}
						>
							<Button
								variant="outlined"
								onClick={() => handleConsent("rejected")}
								sx={{ 
                                    borderRadius: "8px", 
                                    textTransform: "none", 
                                    px: 3,
                                    borderColor: "#EAECF0",
                                    color: "text.primary"
                                }}
							>
								Reject
							</Button>
							<Button
								variant="contained"
								onClick={() => handleConsent("accepted")}
								sx={{ 
                                    borderRadius: "8px", 
                                    textTransform: "none", 
                                    px: 3,
                                    background: "linear-gradient(133deg, #9300FF 7%, #E26AFF 98%)",
                                    boxShadow: "none",
                                    "&:hover": { boxShadow: "none", opacity: 0.9 }
                                }}
							>
								Accept All
							</Button>
						</Box>
					</Grid>
				</Grid>
			</ContainerBlock>
		</BannerWrapper>
	);
};

export default CookieBanner;

import { Grid } from "@mui/material";
import ContainerBlock from "../../core/components/ContainerBlock";
